'use client';

import { useState, type ChangeEvent, useRef } from 'react';
import { Field } from '../../components/field';
import { SettingsEditDialog } from '../settings/SettingsEditDialog';
import { Textarea } from '../../components/textarea';
import { useTranslations } from 'next-intl';
import type { AssetValidationResult } from '@terra/shared-utils';
import { automationId } from '@terra/shared-utils';
import { resolveNewAssetEntries } from './asset-entries';

export type SurfaceScopeAddDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (values: string[]) => void | Promise<void>;
  title: string;
  description: string;
  label: string;
  placeholder: string;
  helperText: string;
  validator: (value: string) => AssetValidationResult;
  duplicateErrorMessage: string;
  existingValues: string[];
  isAdding?: boolean;
  inputTestId?: string;
  confirmTestId?: string;
};

export function SurfaceScopeAddDialog({
  open,
  onOpenChange,
  onAdd,
  title,
  description,
  label,
  placeholder,
  helperText,
  validator,
  duplicateErrorMessage,
  existingValues,
  isAdding = false,
  inputTestId,
  confirmTestId,
}: SurfaceScopeAddDialogProps) {
  const tCommon = useTranslations('designSystem.common');
  const [value, setValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValue('');
      setErrorMessage(null);
    }
  }

  const handleAdd = async () => {
    if (isAdding || isSubmittingRef.current) {
      return;
    }

    const resolution = resolveNewAssetEntries({
      input: value,
      validator,
      existingValues,
      duplicateErrorMessage,
    });

    if (!resolution.ok) {
      setErrorMessage(resolution.message);

      return;
    }

    if (resolution.values.length === 0) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await onAdd(resolution.values);
      onOpenChange(false);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <SettingsEditDialog
      open={open}
      title={title}
      description={description}
      onOpenChange={onOpenChange}
      onSubmit={handleAdd}
      loading={isAdding || isSubmitting}
      isSubmitDisabled={!value.trim()}
      submitText={tCommon('add')}
      submitAutomationId={confirmTestId}
    >
      <Field.Root invalid={errorMessage !== null}>
        <Field.Label>
          <Field.Mandatory /> {label}
        </Field.Label>
        <Textarea
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          minH="120px"
          {...(inputTestId ? automationId(inputTestId) : {})}
        />
        {errorMessage ? (
          <Field.ErrorText>{errorMessage}</Field.ErrorText>
        ) : (
          <Field.HelperText>{helperText}</Field.HelperText>
        )}
      </Field.Root>
    </SettingsEditDialog>
  );
}
